import * as Effects from 'redux-saga/effects'
import { all, call, put, take } from 'redux-saga/effects'
import { addDays } from 'date-fns'
import { callFetch } from '../../actions/fetch'
import { IInvoiceSettings } from '../../models/PaymentDictionary'
import { PaymentDictionaryActions } from '../../reducers/paymentDictionary/actionNames'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import { ActionNames } from '../../utils/FetchActionsName'
import {
  fireToastError,
  fireToastSuccess,
} from '../../actions/toasts/fireToast'
import i18n from '../../i18n'
import loadInvoiceSettings from '../../models/PaymentDictionary/loadInvoiceSettings'
import updateInvoiceSettings from '../../models/PaymentDictionary/updateInvoiceSettings'
import { getInvoiceSettings } from '../../reducers/paymentDictionary/selectors/getPaymentDictionary'
import { IntegrationAction } from '../../reducers/airbnb/actionNames'
import createInvoiceService from '../../models/Invoice/createInvoiceService'
import { UserAccountActions } from '../../reducers/userAccount/actionNames'
import {
  createInvoice,
  createInvoiceFromForm,
} from '../../actions/invoices/createInvoice'
import { InvoiceActions } from '../../reducers/invoice/actionNames'
import fetchInvoice from '../../models/Invoice/fetchInvoice'
import { IInvoice } from '../../models/Invoice'
import { navigateHomeOrNotifyHost } from '../../utils/reactNativeWebViewNavigateHome'
import { openUrlOrNotifyHost } from '../../utils/reactNativeWebViewOpenUrl'

function* loadInvoice() {
  try {
    const actionNames = fetchActionsName(ActionNames.FetchInvoiceSettings)
    const [{ service }] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.put(
        callFetch(actionNames.input, actionNames.output, loadInvoiceSettings),
      ),
    ])

    const settings: IInvoiceSettings = yield Effects.call(service)

    yield Effects.put({
      settings,
      type: PaymentDictionaryActions.AcceptedInvoiceSettings,
    })
  } catch (e) {
    console.error(e)
  }
}

function* saveInvoice(action) {
  try {
    const actionNames = fetchActionsName(ActionNames.UpdateInvoiceSettings)
    const [{ service }] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.put(
        callFetch(actionNames.input, actionNames.output, updateInvoiceSettings),
      ),
    ])

    const invoiceSettings = yield Effects.select(getInvoiceSettings)

    const settings: any = yield Effects.call(service, {
      ...invoiceSettings,
      ...action.settings,
    })

    yield Effects.put({
      settings: settings,
      type: PaymentDictionaryActions.AcceptedInvoiceSettings,
    })
    yield put(fireToastSuccess(i18n.t('user_settings_updated')))
  } catch (e) {
    console.error(e)
  }
}

function* createInvoiceRedux(action: any) {
  try {
    const actionNames = fetchActionsName(ActionNames.CreateInvoiceService)
    const [{ service }] = yield all([
      take(actionNames.output),
      put(
        callFetch(actionNames.input, actionNames.output, createInvoiceService),
      ),
    ])

    const inv = yield call(service)

    yield Effects.put({
      license: {
        id: undefined,
        license: addDays(new Date(), 1),
        licenseType: 7,
        link: inv.items.link,
        rawDate: addDays(new Date(), 1),
        startLicense: addDays(new Date(), 1),
        status: 1,
        trial: 0,
      },
      type: UserAccountActions.InvoiceLicenseDataAccepted,
    })

    if (action.reservationId) {
      if (action.reservationId === '0') {
        yield Effects.put(createInvoiceFromForm(undefined))
      } else {
        yield Effects.put(createInvoice(action.reservationId))
      }
      yield Effects.take(InvoiceActions.Accepted)
    }

    openUrlOrNotifyHost(inv.items.link, 'newtab')
    navigateHomeOrNotifyHost()
  } catch (e) {
    yield Effects.put(fireToastError(i18n.t('errStatus_0a')))
    console.error(e)
  }
}

function* createOrOpenInvoice(action) {
  try {
    const actionNames = fetchActionsName(ActionNames.FetchInvoice)
    const [{ service }] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.put(
        callFetch(actionNames.input, actionNames.output, fetchInvoice),
      ),
    ])

    const invoice: IInvoice = yield Effects.call(service, action.reservationId)

    if (!invoice.link) {
      throw new Error('Invoice link is not defined')
    }
    openUrlOrNotifyHost(editLink(invoice.link, action.edit), 'redirect')
  } catch {
    yield Effects.put(createInvoice(action.reservationId))
    const invoice = yield Effects.take(InvoiceActions.Accepted)

    openUrlOrNotifyHost(editLink(invoice.invoice.link, action.edit), 'redirect')
  }
}

const editLink = (link, edit) => {
  if (edit === '1') {
    const sl = link.split('?')
    return `${sl[0]}/edit?${sl[1]}`
  }
  return link
}

export default function*() {
  yield Effects.takeEvery(
    PaymentDictionaryActions.RequestInvoiceSetting,
    loadInvoice,
  )
  yield Effects.takeEvery(
    PaymentDictionaryActions.SaveInvoiceSetting,
    saveInvoice,
  )
  yield Effects.takeEvery(
    IntegrationAction.CreateInvoiceService,
    createInvoiceRedux,
  )
  yield Effects.takeEvery(
    IntegrationAction.CreateOrOpenInvoice,
    createOrOpenInvoice,
  )
}
